import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FilePlus2, NotebookPen } from 'lucide-react'
import { consultationService } from '@/services/consultation.service'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/hooks/use-toast'

interface ClarifyingNotesSectionProps {
  consultationId: string
  /** Only signed consultations accept addenda. */
  canAdd: boolean
}

/**
 * NOM-004 REQ-INT-03: corrections/clarifications to a signed note are recorded
 * as separate, linked "notas aclaratorias" (addenda) — never by editing or
 * deleting the original.
 */
export function ClarifyingNotesSection({ consultationId, canAdd }: ClarifyingNotesSectionProps) {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [reason, setReason] = useState('')
  const [content, setContent] = useState('')

  const { data: notes = [] } = useQuery({
    queryKey: ['clarifying-notes', consultationId],
    queryFn: () => consultationService.listClarifyingNotes(consultationId),
    enabled: !!consultationId,
  })

  const addNote = useMutation({
    mutationFn: () => consultationService.addClarifyingNote(consultationId, { reason, content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clarifying-notes', consultationId] })
      setReason('')
      setContent('')
      setShowForm(false)
      toast({ title: 'Nota aclaratoria agregada', description: 'La corrección se registró como addendum.' })
    },
    onError: (error: any) => {
      toast({
        title: 'No pudimos agregar la nota',
        description: error.message || error.response?.data?.detail || 'Inténtalo nuevamente.',
        variant: 'destructive',
      })
    },
  })

  // Nothing to show and nothing can be added → render nothing.
  if (notes.length === 0 && !canAdd) return null

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <NotebookPen className="h-4 w-4 text-ml-primary" />
          Notas aclaratorias (addenda)
        </CardTitle>
        {canAdd && !showForm && (
          <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
            <FilePlus2 className="mr-1.5 h-3.5 w-3.5" />
            Agregar
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {notes.length === 0 && (
          <p className="text-xs text-ml-on-surface-variant/60 italic">
            Sin notas aclaratorias. Una nota firmada no se edita; cualquier corrección
            se registra aquí como addendum.
          </p>
        )}

        {notes.map((note) => (
          <div
            key={note.id}
            className="rounded-md border border-ml-outline-variant/20 bg-ml-surface-container/30 px-3 py-2"
          >
            <p className="text-xs font-semibold text-ml-on-surface">{note.reason}</p>
            <p className="text-sm text-ml-on-surface whitespace-pre-wrap mt-0.5">{note.content}</p>
            <p className="text-[11px] text-ml-on-surface-variant/60 mt-1">
              {note.author_name}
              {note.author_cedula ? ` · Céd. ${note.author_cedula}` : ''}
              {note.signed_at ? ` · ${new Date(note.signed_at).toLocaleString('es-MX')}` : ''}
            </p>
          </div>
        ))}

        {showForm && (
          <div className="space-y-2 border-t border-ml-outline-variant/20 pt-3">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo de la aclaración (p. ej. corrección de dosis)"
            />
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Contenido de la nota aclaratoria…"
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={reason.trim().length < 5 || content.trim().length < 5 || addNote.isPending}
                onClick={() => addNote.mutate()}
              >
                {addNote.isPending ? 'Guardando…' : 'Firmar addendum'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
